/**
 * User Role Select Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * Dropdown selector for user roles with descriptions.
 */

import React from 'react';
import type { UserRole } from '../../../types/admin';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '../../../types/admin';

interface UserRoleSelectProps {
  value: UserRole;
  onChange: (role: UserRole) => void;
  disabled?: boolean;
  showDescription?: boolean;
  className?: string;
}

const ROLES: UserRole[] = ['admin', 'advisor', 'viewer', 'compliance_officer'];

const UserRoleSelect: React.FC<UserRoleSelectProps> = ({
  value,
  onChange,
  disabled = false,
  showDescription = false,
  className = '',
}) => {
  if (showDescription) {
    // Radio button style with descriptions
    return (
      <div className={`space-y-2 ${className}`}>
        {ROLES.map((role) => (
          <label
            key={role}
            className={`
              flex items-start gap-3 p-3 rounded-lg border cursor-pointer
              transition-colors
              ${value === role
                ? 'border-[#002B45] bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              type="radio"
              name="role"
              value={role}
              checked={value === role}
              onChange={() => onChange(role)}
              disabled={disabled}
              className="mt-1 text-[#002B45] focus:ring-[#F7B500]"
            />
            <div>
              <span className="font-medium text-gray-900">{ROLE_LABELS[role]}</span>
              <p className="text-sm text-gray-500 mt-0.5">{ROLE_DESCRIPTIONS[role]}</p>
            </div>
          </label>
        ))}
      </div>
    );
  }

  // Simple dropdown
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as UserRole)}
      disabled={disabled}
      className={`
        block w-full px-3 py-2 text-sm
        border border-gray-300 rounded-lg
        focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      {ROLES.map((role) => (
        <option key={role} value={role}>
          {ROLE_LABELS[role]}
        </option>
      ))}
    </select>
  );
};

export default UserRoleSelect;
